import { ButtonBox, Flex, Loader, OutsideClick, Paginator, Text } from '@upsales/components';
import RequestBuilder from 'Resources/RequestBuilder';
import ComparisonTypes from 'Resources/ComparisonTypes';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import BemClass from '@upsales/components/Utils/bemClass';
import FileStorageApps from 'Components/FileStorageApps';
import { HTML5Backend } from 'react-dnd-html5-backend';
import FoldersResource from 'Resources/FileFolder';
import FileResource from 'Resources/File';
import Client from 'App/resources/Model/Client';
import T from 'Components/Helpers/translate';
import openModal from 'App/services/Modal';
import FolderModal from './FolderModal';
import logError from 'Helpers/logError';
import { DndProvider } from 'react-dnd';
import GridView from './GridView';
import ListView from './ListView';
import Header from './Header';

import './AccountFiles.scss';

export type FolderType = {
	id: number;
	name: string;
	folderId: number | null;
};

export type FolderTreeType = {
	id: number;
	name: string;
};

export type FileType = {
	client: { id: number };
	id: number;
	filename: string;
	size: number;
	extension: string;
	uploadDate: string;
	user: { id: number };
	driveFolderId: number;
	entityId?: number;
	entity?: string;
	userRemovable: boolean;
};

export type SortState = {
	field: string;
	asc: boolean;
};

const PAGE_SIZE = 50;

const AccountFiles = ({ client }: { client: Client }) => {
	const classes = new BemClass('AccountFiles');

	const [folders, setFolders] = useState<FolderType[]>([]);
	const [files, setFiles] = useState<FileType[]>([]);
	const [fileMeta, setFileMeta] = useState<{ total: number; offset: number; limit: number } | null>(null);
	const [folderMeta, setFolderMeta] = useState<{ total: number; offset: number; limit: number } | null>(null);
	const [folderTree, setFolderTree] = useState<FolderTreeType[]>([]);
	const [listMode, setListMode] = useState(true);
	const [initialLoadDone, setInitialLoadDone] = useState(false);
	const [showAddFolderModal, setShowAddFolderModal] = useState(false);
	const [searchString, setSearchString] = useState<string | null>(null);
	const [sort, setSort] = useState<SortState>({ field: 'filename', asc: true });
	const [currentOffset, setCurrentOffset] = useState(0);
	const [folderOffset, setFolderOffset] = useState(0);

	const currentFolderId = folderTree[folderTree.length - 1]?.id ?? null;
	const fetchIdRef = useRef(0);

	const fetchFolderContents = useCallback(async () => {
		if (!client.id) return;

		const fetchId = ++fetchIdRef.current;

		const isSearching = searchString !== null && searchString.length > 0;

		const folderRb = new RequestBuilder();
		folderRb.addFilter({ field: 'clientId' }, ComparisonTypes.Equals, client.id);
		if (isSearching) {
			folderRb.addFilter({ field: 'name' }, ComparisonTypes.Wildcard, searchString);
		} else {
			folderRb.addFilter({ field: 'folderId' }, ComparisonTypes.Equals, currentFolderId ?? null);
		}
		folderRb.addSort('name', true);
		folderRb.limit = PAGE_SIZE;
		folderRb.offset = folderOffset;

		const fileRb = new RequestBuilder();
		fileRb.addFilter({ field: 'client.id' }, ComparisonTypes.Equals, client.id);
		if (isSearching) {
			fileRb.addFilter({ field: 'filename' }, ComparisonTypes.Wildcard, searchString);
		} else if (currentFolderId) {
			fileRb.addFilter({ field: 'driveFolderId' }, ComparisonTypes.Equals, currentFolderId);
		} else {
			// Root level: files have driveFolderId = 0 or NULL in the DB
			const or = fileRb.orBuilder();
			or.next();
			or.addFilter({ field: 'driveFolderId' }, ComparisonTypes.Equals, 0);
			or.next();
			or.addFilter({ field: 'driveFolderId' }, ComparisonTypes.Equals, null);
			or.done();
		}
		fileRb.addSort(sort.field, sort.asc);
		fileRb.limit = PAGE_SIZE;
		fileRb.offset = currentOffset;

		try {
			const [folderRes, fileRes] = await Promise.all([
				FoldersResource.find(folderRb.build()),
				FileResource.find(fileRb.build())
			]);

			if (fetchId !== fetchIdRef.current) return;

			setFolders(folderRes.data);
			setFolderMeta(folderRes.metadata);
			setFiles(fileRes.data);
			setFileMeta(fileRes.metadata);
		} catch (e) {
			logError(e, 'Could not fetch files and folders');
		} finally {
			if (fetchId === fetchIdRef.current) {
				setInitialLoadDone(true);
			}
		}
	}, [client.id, currentFolderId, searchString, sort, currentOffset, folderOffset]);

	useEffect(() => {
		fetchFolderContents();
	}, [fetchFolderContents]);

	useEffect(() => {
		if (localStorage.getItem('boxViewDocuments') === '1') {
			setListMode(false);
		}
	}, []);

	useEffect(() => {
		const listener = Tools.$rootScope.$on('file.uploaded', () => {
			fetchFolderContents();
		});
		return () => listener();
	}, [fetchFolderContents]);

	const buildFolderTree = (folderId: number) => {
		const folderRb = new RequestBuilder();
		folderRb.addFilter({ field: 'clientId' }, ComparisonTypes.Equals, client.id);

		FoldersResource.find(folderRb.build())
			.then((res: { data: FolderType[] }) => {
				const allFolders = res.data;
				const tree: FolderTreeType[] = [];
				let id: number | null = folderId;
				while (id) {
					const parentFolder = allFolders.find(folder => folder.id === id);
					if (parentFolder) {
						tree.unshift({ id: parentFolder.id, name: parentFolder.name });
						id = parentFolder.folderId;
					} else {
						break;
					}
				}
				setFolderTree(tree);
				setSearchString(null);
			})
			.catch((e: unknown) => logError(e, 'Could not build folder tree'));
	};

	const openFolder = (folderId: number, name: string) => {
		if (searchString !== null && folderId) {
			buildFolderTree(folderId);
			return;
		}
		setFolderTree((currentTree: FolderTreeType[]) => [...currentTree, { id: folderId, name }]);
		setCurrentOffset(0);
		setFolderOffset(0);
	};

	const changeFolderFromTree = (folderId?: number | null) => {
		if (folderId === currentFolderId) return;

		setFolderTree((currentTree: FolderTreeType[]) => {
			const indexOfFolder = currentTree.findIndex(folder => folder.id === folderId);
			const newfolderTree = [...currentTree];
			newfolderTree.length = indexOfFolder + 1;
			return newfolderTree;
		});
		setCurrentOffset(0);
		setFolderOffset(0);
	};

	const createFolder = (name: string) => {
		if (!client.id) return;

		FoldersResource.save({ name, clientId: client.id, folderId: currentFolderId })
			.then(() => {
				Tools.NotificationService.addNotification({
					style: Tools.NotificationService.style.SUCCESS,
					icon: 'check',
					title: 'default.done',
					body: 'folder.folderCreated'
				});
				fetchFolderContents();
			})
			.catch((e: unknown) => {
				logError(e, 'Could not create folder');
				Tools.NotificationService.addNotification({
					style: Tools.NotificationService.style.ERROR,
					icon: 'times',
					title: 'default.error',
					body: 'folder.folderCreateFailed'
				});
			});
	};

	const changeFolderFolder = (id: number, folderId: number | null) => {
		if (!client.id) return;

		FoldersResource.save({ clientId: client.id, id, folderId })
			.then(() => {
				Tools.NotificationService.addNotification({
					style: Tools.NotificationService.style.SUCCESS,
					icon: 'check',
					title: 'default.done',
					body: 'folder.folderMoved'
				});
				fetchFolderContents();
			})
			.catch((e: unknown) => {
				logError(e, 'Could not move folder');
				Tools.NotificationService.addNotification({
					style: Tools.NotificationService.style.ERROR,
					icon: 'times',
					title: 'default.error',
					body: 'folder.folderMoveFailed'
				});
			});
	};

	const changeFileFolder = (id: number, folderId: number | null) => {
		FoldersResource.changeFileFolder(id, folderId)
			.then(() => {
				Tools.NotificationService.addNotification({
					style: Tools.NotificationService.style.SUCCESS,
					icon: 'check',
					title: 'default.done',
					body: 'file.fileMoved'
				});
				fetchFolderContents();
			})
			.catch(e => {
				logError(e, 'Could not move file');
				Tools.NotificationService.addNotification({
					style: Tools.NotificationService.style.ERROR,
					icon: 'times',
					title: 'default.error',
					body: 'file.fileMoveFailed'
				});
			});
	};

	const addFile = () => {
		if (!client.id) return;

		if (Tools.FeatureHelper.hasSoftDeployAccess('REACT_UPLOAD_FILE_MODAL')) {
			return openModal('UploadFileModal', {
				accountId: client.id,
				folderId: currentFolderId,
				closeOnUpload: true
			});
		}
		Tools.$upModal
			.open('uploadFile', {
				accountId: client.id,
				folderId: currentFolderId,
				closeOnUpload: true
			})
			.catch(e => logError(e, 'Could not upload file'));
	};

	const deleteFolder = (id: number) => {
		FoldersResource.delete(id)
			.then(() => {
				Tools.NotificationService.addNotification({
					style: Tools.NotificationService.style.SUCCESS,
					icon: 'check',
					title: 'default.done',
					body: 'folder.folderDeleted'
				});
				fetchFolderContents();
			})
			.catch((err: { response?: { status: number } }) => {
				if (err.response?.status === 400) {
					Tools.NotificationService.addNotification({
						style: Tools.NotificationService.style.ERROR,
						icon: 'times',
						title: 'default.error',
						body: 'foler.folderNotEmpty'
					});
				} else {
					logError(err, 'Could not delete folder');
				}
			});
	};

	const deleteFile = (file: FileType) => {
		Tools.File.delete(file, { noConfirm: true })
			.then(res => {
				if (!res) return;
				fetchFolderContents();
			})
			.catch((e: unknown) => {
				logError(e, 'Could not delete file');
				Tools.NotificationService.addNotification({
					style: Tools.NotificationService.style.ERROR,
					icon: 'times',
					title: 'default.error',
					body: 'deleteError.file'
				});
			});
	};

	const changeNameOfFolder = (id: number, name: string) => {
		if (!client.id) return;

		FoldersResource.save({ name, clientId: client.id, id })
			.then(() => {
				setFolderTree((currentFolderTree: FolderTreeType[]) =>
					currentFolderTree.map((folder: FolderTreeType) => {
						if (folder.id === id) {
							return { ...folder, name };
						}
						return folder;
					})
				);
				Tools.NotificationService.addNotification({
					style: Tools.NotificationService.style.SUCCESS,
					icon: 'check',
					title: 'default.done',
					body: 'folder.folderNameChanged'
				});
				fetchFolderContents();
			})
			.catch((e: unknown) => {
				logError(e, 'Could not change name');
				Tools.NotificationService.addNotification({
					style: Tools.NotificationService.style.ERROR,
					icon: 'times',
					title: 'default.error',
					body: 'folder.folderNameChangeFailed'
				});
			});
	};

	const onSortChange = (newSort: SortState) => {
		setSort(newSort);
		setCurrentOffset(0);
		setFolderOffset(0);
	};

	const content = () => {
		if (!files.length && !folders.length) {
			let element;
			if (searchString) {
				element = <Text size="lg">{T('noResult.generic')}</Text>;
			} else if (showAddFolderModal) {
				element = (
					<OutsideClick outsideClick={() => setShowAddFolderModal(false)} targetClass={'FolderModal'}>
						<div>
							<FolderModal
								animate
								noDropDown
								onClick={name => {
									createFolder(name);
									setShowAddFolderModal(false);
								}}
							/>
						</div>
					</OutsideClick>
				);
			} else {
				element = (
					<>
						<ButtonBox selectable={false} onClick={addFile} icon="file" title={T('file.addFile')} />
						<ButtonBox
							selectable={false}
							onClick={() => setShowAddFolderModal(true)}
							icon="folder"
							title={T('file.addFolder')}
						/>
					</>
				);
			}

			return (
				<Flex className={classes.elem('center').b()} gap="u10" justifyContent="center" alignItems="center">
					{element}
				</Flex>
			);
		}

		const folderPaginatorEl =
			folderMeta && folderMeta.total > PAGE_SIZE ? (
				<Paginator
					limit={PAGE_SIZE}
					offset={folderOffset}
					total={folderMeta.total}
					onChange={setFolderOffset}
					align="center"
				/>
			) : null;

		const filesViewProps = {
			folders,
			files,
			openFolder,
			deleteFolder,
			deleteFile,
			changeFolderFolder,
			changeFileFolder,
			changeNameOfFolder,
			clientId: client.id,
			sort,
			onSortChange,
			folderPaginator: folderPaginatorEl
		};

		if (listMode) {
			return <ListView {...filesViewProps} />;
		}
		return <GridView {...filesViewProps} />;
	};

	if (!initialLoadDone) {
		return (
			<Flex className={classes.elem('center-loader').b()} justifyContent="center" alignItems="center">
				<Loader size="xl" />
			</Flex>
		);
	}
	return (
		<div className={classes.b()}>
			<DndProvider backend={HTML5Backend}>
				<Header
					folders={folderTree}
					addFolder={createFolder}
					addFile={addFile}
					changeFolder={changeFolderFromTree}
					setSearchString={setSearchString}
					searchString={searchString}
					setListMode={setListMode}
					listMode={listMode}
					changeFolderFolder={changeFolderFolder}
					changeFileFolder={changeFileFolder}
					numberOfFolders={folders.length}
					numberOfFiles={fileMeta?.total ?? files.length}
				/>
				{content()}
				{fileMeta && fileMeta.total > PAGE_SIZE ? (
					<Paginator
						limit={PAGE_SIZE}
						offset={currentOffset}
						total={fileMeta.total}
						onChange={setCurrentOffset}
						align="center"
					/>
				) : null}
				{Tools.AppService.getFileStorageIntegrations().length > 0 ? (
					<div className={classes.elem('google-drive-container').b()}>
						<FileStorageApps
							apps={Tools.AppService.getFileStorageIntegrations()}
							objectId={client.id}
							type="client"
							displayTypeFromParent={listMode ? 'list' : 'grid'}
						/>
					</div>
				) : null}
			</DndProvider>
		</div>
	);
};

export default AccountFiles;
